import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { MapPin, Package, ArrowRight, BadgeCheck, Building2 } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Premium supplier images by country
const SUPPLIER_IMAGES = {
  'Nigeria': 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?w=400&h=300&fit=crop',
  'Ghana': 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?w=400&h=300&fit=crop',
  'Kenya': 'https://images.pexels.com/photos/3184431/pexels-photo-3184431.jpeg?w=400&h=300&fit=crop',
  'South Africa': 'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?w=400&h=300&fit=crop',
  'Egypt': 'https://images.pexels.com/photos/3184639/pexels-photo-3184639.jpeg?w=400&h=300&fit=crop',
  'default': 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?w=400&h=300&fit=crop'
};

const getSupplierImage = (supplier) => {
  if (supplier.logo) return supplier.logo;
  return SUPPLIER_IMAGES[supplier.country] || SUPPLIER_IMAGES.default;
};

export const SupplierStorefrontsSection = () => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await axios.get(`${API_URL}/suppliers?limit=12&verified=true`);
        setSuppliers(res.data.suppliers || []);
      } catch (error) {
        // Suppliers fetch failed - section won't show, log in dev
        if (process.env.NODE_ENV === 'development') {
          console.log('[SupplierStorefronts] Fetch failed:', error.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSuppliers();
  }, []);

  if (loading || suppliers.length === 0) return null;

  // Double the suppliers for infinite scroll effect
  const marqueeSuppliers = [...suppliers, ...suppliers];

  return (
    <section className="py-24 sm:py-32 bg-white overflow-hidden" data-testid="supplier-storefronts">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 mb-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6"
        >
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1E3A8A] mb-3">
              {t('home.suppliers.verifiedPartners')}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] tracking-tight">
              {t('home.suppliers.title')}
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl text-lg">
              {t('home.suppliers.subtitle')}
            </p>
          </div>
          <Link to="/suppliers">
            <Button 
              variant="outline" 
              className="gap-2 border-2 border-slate-200 hover:border-[#1E3A8A] hover:text-[#1E3A8A] px-6 py-3 rounded-xl font-medium transition-all"
              data-testid="view-all-suppliers-btn"
            >
              {t('home.suppliers.viewAll')} <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Marquee Container */}
      <div className="relative">
        {/* Left Fade */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        {/* Right Fade */}
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
        
        {/* Marquee Track */}
        <div className="flex gap-6 animate-marquee hover:[animation-play-state:paused]">
          {marqueeSuppliers.map((supplier, idx) => (
            <Link 
              key={`${supplier.id}-${idx}`}
              to={`/suppliers/${supplier.id}`}
              className="flex-shrink-0 w-80 group"
              data-testid={`supplier-card-${supplier.id}`}
            >
              <div className="bg-white rounded-2xl overflow-hidden card-premium border border-slate-100 h-full">
                {/* Header Banner */}
                <div className="relative h-28 bg-gradient-to-br from-[#C25E30]/10 via-orange-50 to-amber-50">
                  <div className="absolute inset-0 opacity-5" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C25E30' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }} />
                  
                  {/* Verified Badge */}
                  {supplier.verified && (
                    <div className="absolute top-3 right-3">
                      <span className="bg-green-500 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full flex items-center gap-1 shadow-sm">
                        <BadgeCheck className="w-3.5 h-3.5" />
                        {t('home.suppliers.verified')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Logo */}
                <div className="relative px-5 -mt-10">
                  <div className="w-20 h-20 rounded-2xl border-4 border-white bg-white shadow-lg overflow-hidden group-hover:shadow-xl transition-shadow">
                    <img 
                      src={getSupplierImage(supplier)} 
                      alt={supplier.company_name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = SUPPLIER_IMAGES.default; }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 pt-4">
                  <h3 className="font-semibold text-lg text-[#0A0A0A] group-hover:text-[#C25E30] transition-colors line-clamp-1">
                    {supplier.company_name}
                  </h3>
                  
                  <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                    <MapPin className="w-4 h-4 text-[#C25E30]" />
                    <span>{supplier.country}</span>
                    {supplier.business_type && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span className="capitalize">{supplier.business_type.replace('_', ' ')}</span>
                      </>
                    )}
                  </div>

                  {supplier.description && (
                    <p className="text-sm text-slate-500 mt-3 line-clamp-2 leading-relaxed">
                      {supplier.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Package className="w-4 h-4" />
                      <span>{supplier.product_count || 0} {t('home.suppliers.products')}</span>
                    </div>
                    <span className="text-[#C25E30] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      {t('home.suppliers.viewStore')} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 mt-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {[
            { value: '40+', labelKey: 'home.suppliers.statsVerified', icon: Building2 },
            { value: '54', labelKey: 'home.suppliers.statsCountries', icon: MapPin },
            { value: '150+', labelKey: 'home.suppliers.statsProducts', icon: Package },
            { value: '100%', labelKey: 'home.suppliers.statsPartners', icon: BadgeCheck },
          ].map((stat, idx) => (
            <div 
              key={stat.labelKey}
              className="text-center p-6 rounded-2xl bg-slate-50/50 border border-slate-100"
            >
              <stat.icon className="w-6 h-6 mx-auto mb-3 text-[#C25E30]" />
              <p className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">{stat.value}</p>
              <p className="text-sm text-slate-500 mt-1">{t(stat.labelKey)}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
